import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type ReactNode } from "react";
import { isImageFile, uploadImage } from "../../lib/images";

type Props = {
  value: string;
  onChange: (value: string) => void;
  renderPreview: (value: string) => ReactNode;
  label: string;
  hint?: string;
  required?: boolean;
  rows?: number;
};

type View = "split" | "write" | "preview";

type Action = {
  key: string;
  label: string;
  title: string;
  run: (selection: string) => { text: string; select?: [number, number] };
  block?: boolean;
};

const wrap = (before: string, after: string, placeholder: string): Action["run"] => (selection) => {
  const body = selection || placeholder;
  return { text: `${before}${body}${after}`, select: [before.length, before.length + body.length] };
};

const lines = (prefix: string | ((index: number) => string), placeholder: string): Action["run"] => (selection) => {
  const source = (selection || placeholder).split("\n");
  const text = source.map((line, index) => `${typeof prefix === "string" ? prefix : prefix(index)}${line}`).join("\n");
  return { text, select: [0, text.length] };
};

const actions: Action[] = [
  { key: "bold", label: "B", title: "Bold", run: wrap("**", "**", "bold text") },
  { key: "italic", label: "I", title: "Italic", run: wrap("_", "_", "italic text") },
  { key: "code", label: "</>", title: "Inline code", run: wrap("`", "`", "code") },
  { key: "h2", label: "H2", title: "Section heading", run: lines("## ", "Section"), block: true },
  { key: "h3", label: "H3", title: "Subheading", run: lines("### ", "Subsection"), block: true },
  { key: "quote", label: "❝", title: "Quote", run: lines("> ", "Quoted text"), block: true },
  { key: "ul", label: "•", title: "Bullet list", run: lines("- ", "List item"), block: true },
  { key: "ol", label: "1.", title: "Numbered list", run: lines((index) => `${index + 1}. `, "List item"), block: true },
  { key: "task", label: "☑", title: "Task list", run: lines("- [ ] ", "To do"), block: true },
  { key: "link", label: "Link", title: "Link", run: (selection) => ({ text: `[${selection || "link text"}](https://)`, select: [1, (selection || "link text").length + 1] }) },
  {
    key: "table",
    label: "Table",
    title: "Table",
    block: true,
    run: () => ({ text: "| Column | Column |\n| --- | --- |\n| Value | Value |" })
  },
  { key: "fence", label: "Code", title: "Code block", block: true, run: (selection) => ({ text: `\`\`\`ts\n${selection || "const value = 1;"}\n\`\`\`` }) },
  {
    key: "mermaid",
    label: "Diagram",
    title: "Mermaid diagram",
    block: true,
    run: (selection) => ({ text: `\`\`\`mermaid\n${selection || "flowchart LR\n  A[Request] --> B{Admin?}\n  B -- yes --> C[Save]\n  B -- no --> D[Reject]"}\n\`\`\`` })
  },
  { key: "math", label: "∑", title: "Maths block", block: true, run: (selection) => ({ text: `$$\n${selection || "e = mc^2"}\n$$` }) }
];

export function MarkdownField({ value, onChange, renderPreview, label, hint, required, rows = 18 }: Props) {
  const [view, setView] = useState<View>("split");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [debounced, setDebounced] = useState(value);
  const area = useRef<HTMLTextAreaElement>(null);
  const preview = useRef<HTMLDivElement>(null);
  const latest = useRef(value);
  const caret = useRef<[number, number] | null>(null);

  latest.current = value;

  // The preview trails typing slightly: diagrams and formulas are expensive to redraw.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), 250);
    return () => window.clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    const selection = caret.current;
    if (!selection || !area.current) return;
    caret.current = null;
    area.current.focus();
    area.current.setSelectionRange(selection[0], selection[1]);
  });

  function replaceRange(start: number, end: number, text: string, select?: [number, number]) {
    const next = `${latest.current.slice(0, start)}${text}${latest.current.slice(end)}`;
    caret.current = select ? [start + select[0], start + select[1]] : [start + text.length, start + text.length];
    latest.current = next;
    onChange(next);
  }

  function run(action: Action) {
    const element = area.current;
    if (!element) return;
    const source = latest.current;
    const start = element.selectionStart;
    const end = element.selectionEnd;
    const result = action.run(source.slice(start, end));
    if (!action.block) {
      replaceRange(start, end, result.text, result.select);
      return;
    }
    const before = source.slice(0, start);
    const lead = !before || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
    const after = source.slice(end);
    const trail = !after || after.startsWith("\n") ? "" : "\n";
    const text = `${lead}${result.text}${trail}`;
    replaceRange(start, end, text, result.select ? [lead.length + result.select[0], lead.length + result.select[1]] : undefined);
  }

  async function insertFiles(files: File[]) {
    const images = files.filter(isImageFile);
    if (!images.length) return;
    const element = area.current;
    const at = element ? element.selectionEnd : latest.current.length;
    setError("");
    setStatus(images.length > 1 ? `Uploading ${images.length} images…` : "Uploading image…");
    const links: string[] = [];
    try {
      for (const file of images) {
        const url = await uploadImage(file);
        links.push(`![${file.name.replace(/\.[a-z0-9]+$/i, "").replace(/[[\]]/g, "")}](${url})`);
      }
      const before = latest.current.slice(0, at);
      const lead = !before || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
      replaceRange(at, at, `${lead}${links.join("\n\n")}\n`);
      setStatus(links.length > 1 ? `${links.length} images added.` : "Image added.");
      window.setTimeout(() => setStatus(""), 2500);
    } catch (cause) {
      setStatus("");
      setError(cause instanceof Error ? cause.message : "Unable to upload this image.");
    }
  }

  function onPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = Array.from(event.clipboardData?.files ?? []) as File[];
    if (!files.length) return;
    event.preventDefault();
    void insertFiles(files);
  }

  function onDrop(event: DragEvent<HTMLTextAreaElement>) {
    const files = Array.from(event.dataTransfer?.files ?? []) as File[];
    if (!files.length) return;
    event.preventDefault();
    void insertFiles(files);
  }

  function syncScroll() {
    const source = area.current;
    const target = preview.current;
    if (!source || !target || view !== "split") return;
    const range = source.scrollHeight - source.clientHeight;
    if (range <= 0) return;
    target.scrollTop = (source.scrollTop / range) * (target.scrollHeight - target.clientHeight);
  }

  return (
    <section className="bo-md">
      <div className="bo-md-bar">
        <div className="bo-md-actions">
          {actions.map((action) => (
            <button key={action.key} type="button" title={action.title} aria-label={action.title} onClick={() => run(action)}>
              {action.label}
            </button>
          ))}
          <label className="bo-md-upload" title="Upload an image">
            Image
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => {
                void insertFiles(Array.from(event.target.files ?? []) as File[]);
                event.target.value = "";
              }}
            />
          </label>
        </div>
        <div className="bo-md-views">
          {(["write", "split", "preview"] as View[]).map((mode) => (
            <button key={mode} type="button" aria-pressed={view === mode} onClick={() => setView(mode)}>
              {mode === "write" ? "Write" : mode === "split" ? "Split" : "Preview"}
            </button>
          ))}
        </div>
      </div>

      {status ? <p className="bo-md-status" role="status">{status}</p> : null}
      {error ? <p className="bo-error" role="alert">{error}</p> : null}

      <div className="bo-md-panes" data-view={view}>
        <label className="bo-md-write">
          <span className="bo-md-legend">{label}</span>
          <textarea
            ref={area}
            // A required control inside a hidden pane would block the form from ever submitting.
            required={required && view !== "preview"}
            maxLength={200000}
            rows={rows}
            className="bo-markdown"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onPaste={onPaste}
            onDrop={onDrop}
            onScroll={syncScroll}
            spellCheck
          />
          {hint ? <small>{hint}</small> : null}
        </label>
        <div className="bo-md-read">
          <span className="bo-md-legend">Preview</span>
          <div className="bo-preview" ref={preview}>
            {renderPreview(debounced)}
          </div>
        </div>
      </div>
    </section>
  );
}
